import React, { useEffect, useId, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { ArrowExpand20Regular } from '@fluentui/react-icons';

import DiagramViewer from '@/components/DiagramViewer';

/* ------------------------------------------------------------------ */
/*  MermaidDiagram                                                     */
/*                                                                     */
/*  Renders a ```mermaid code block (emitted by the RAG agent) as an   */
/*  inline SVG. While the message is still streaming the source is     */
/*  shown as plain code, since a partial diagram never parses. On a    */
/*  parse error the raw source is shown with the error so the user can */
/*  still read what the model produced.                                */
/* ------------------------------------------------------------------ */

// Alpilink palette (see identite-chatbot.md / index.css :root)
mermaid.initialize({
  startOnLoad: false,
  securityLevel: 'strict',
  fontFamily: 'Inter, "Segoe UI", sans-serif',
  theme: 'base',
  themeVariables: {
    primaryColor: '#DCE0F0',        // lavande — node fill
    primaryTextColor: '#1F2F66',    // navy royal
    primaryBorderColor: '#304697',  // royal interactif
    lineColor: '#304697',
    secondaryColor: '#F3F3F3',
    tertiaryColor: '#FFFFFF',
    fontSize: '14px',
  },
});

/**
 * The mistake a small model makes most: a label holding ( ) or : left unquoted,
 * which breaks the whole diagram. Quoting it is always safe. Only applied after a
 * failed parse, so a valid diagram is never rewritten.
 */
const quoteLabels = (code: string): string =>
  code
    .replace(/(\b[A-Za-z_][\w-]*)\[(?!["[(/\\])([^\]"\n]+)\]/g, '$1["$2"]')
    .replace(/\|(?!")([^|"\n]+)\|/g, '|"$1"|');

interface MermaidDiagramProps {
  code: string;
  isStreaming?: boolean;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ code, isStreaming }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  // mermaid.render needs a DOM-unique id; useId gives one per component instance
  const renderId = `mermaid-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    if (isStreaming) return;

    let cancelled = false;
    setError(null);

    (async () => {
      const source = code.trim();
      try {
        const { svg: rendered } = await mermaid.render(renderId, source);
        if (!cancelled) setSvg(rendered);
      } catch (err) {
        // mermaid leaves a stray element behind on parse failure
        document.getElementById(renderId)?.remove();
        const repaired = quoteLabels(source);
        if (repaired !== source) {
          try {
            const { svg: rendered } = await mermaid.render(`${renderId}-retry`, repaired);
            if (!cancelled) setSvg(rendered);
            return;
          } catch {
            document.getElementById(`${renderId}-retry`)?.remove();
          }
        }
        if (cancelled) return;
        setSvg('');
        setError(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, isStreaming, renderId]);

  // A partial diagram never parses, and its source means nothing to a visitor
  if (isStreaming || (!svg && !error)) {
    return (
      <div className="mermaid-pending" role="status">
        <span className="mermaid-pending-dot" aria-hidden="true" />
        Schéma en cours de préparation…
      </div>
    );
  }

  if (error) {
    return (
      <div className="mermaid-error">
        <div className="mermaid-error-title">Schéma non affichable (syntaxe Mermaid invalide)</div>
        <pre className="mermaid-source">
          <code>{code}</code>
        </pre>
      </div>
    );
  }

  const open = () => setExpanded(true);

  return (
    <>
      <div
        ref={containerRef}
        className="mermaid-diagram"
        role="button"
        tabIndex={0}
        title="Agrandir le schéma"
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        }}
      >
        {/* SVG produced by mermaid with securityLevel 'strict' (labels are escaped) */}
        <div className="mermaid-svg" dangerouslySetInnerHTML={{ __html: svg }} />
        <span className="mermaid-expand" aria-hidden="true">
          <ArrowExpand20Regular /> Agrandir
        </span>
      </div>
      {expanded && <DiagramViewer svg={svg} onClose={() => setExpanded(false)} />}
    </>
  );
};

export default MermaidDiagram;
